import React, { useState } from 'react'

import { FormattedMessage } from 'react-intl'

import AudienceShareBarChart from '@components/Charts/AudienceShareBarChart'
import { IBarData } from '@components/Charts/AudienceShareBarChart/AudienceShareBarChart.types'
import PackedBubbleChart from '@components/Charts/PackedBubbleChart'
import { IBubbleData } from '@components/Charts/PackedBubbleChart/PackedBubbleChart.types'
import { IBrandConnectAudienceInterestInsight } from '@store/audienceInsights'
import Theme from '@theme'
import { Container, H5, Radio } from '@tribegroup'
import { FullWidthChart } from './AudienceInsights.styled'

interface IInterestSectionProps {
  titleI18nKey: string
  data: ReadonlyArray<IBrandConnectAudienceInterestInsight>
}

type ViewMode = 'audienceShare' | 'relativeRelevance'

// Relative Relevance color scale: Hot to Cold (Highest to Lowest) — supports up to 15 categories
const RELATIVE_RELEVANCE_COLORS = [
  '#C33400', // Hottest (highest value)
  '#D44A00',
  '#D67600',
  '#E09800',
  '#EED408',
  '#A5C800',
  '#4BA83E',
  '#009E82',
  '#007DD6',
  '#0054A8',
  '#3C2FA8',
  '#6318B8',
  '#7800C3',
  '#9500A8',
  '#A3007D', // Coldest (lowest value)
]

const InterestSection: React.FC<IInterestSectionProps> = ({ titleI18nKey, data }) => {
  const [viewMode, setViewMode] = useState<ViewMode>('audienceShare')

  // Filter and sort data by relative_relevance (highest to lowest) for color mapping
  const validDataWithRelevance = data.filter(
    (item) => typeof item.relative_relevance === 'number' && !isNaN(item.relative_relevance),
  )
  const sortedDataByRelevance = [...validDataWithRelevance].sort(
    (a, b) => (b.relative_relevance || 0) - (a.relative_relevance || 0),
  )

  // Convert array data to chart formats
  const bubbleData: IBubbleData[] = sortedDataByRelevance.map((item, index) => ({
    label: item.label,
    audienceSharePercentage: item.percentage / 100, // Convert from percentage to decimal (0-1 range)
    relativeRelevance: item.relative_relevance || 0,
    color: RELATIVE_RELEVANCE_COLORS[index % RELATIVE_RELEVANCE_COLORS.length], // Map colors hot to cold
  }))

  const barData: IBarData[] = data.map((item) => ({
    label: item.label,
    audienceSharePercentage: item.percentage / 100, // Convert from percentage to decimal (0-1 range)
  }))

  return (
    <FullWidthChart data-testid={`interest-section-${titleI18nKey}`}>
      {/* Header with Radio Toggle */}
      <Container flexBetween bottomOuterSpacing={1.5}>
        <H5 color={Theme.grey900Color}>
          <FormattedMessage id={titleI18nKey} />
        </H5>

        {/* Radio Toggle */}
        <Container flexStart gap={1}>
          <Radio
            scheme="primary"
            name={`view-mode-${titleI18nKey}`}
            value="audienceShare"
            checked={viewMode === 'audienceShare'}
            onChange={() => setViewMode('audienceShare')}
            label={<FormattedMessage id="audienceInsights.view_mode.audience_share" />}
            labelFontSize={0.875}
            data-testid={`radio-audience-share-${titleI18nKey}`}
          />
          <Radio
            scheme="primary"
            name={`view-mode-${titleI18nKey}`}
            value="relativeRelevance"
            checked={viewMode === 'relativeRelevance'}
            onChange={() => setViewMode('relativeRelevance')}
            label={<FormattedMessage id="audienceInsights.view_mode.relative_relevance" />}
            labelFontSize={0.875}
            data-testid={`radio-relative-relevance-${titleI18nKey}`}
          />
        </Container>
      </Container>

      {/* Chart Display */}
      <div data-testid={`chart-container-${viewMode}`}>
        {viewMode === 'relativeRelevance' && (
          <PackedBubbleChart
            data={bubbleData}
            height={Math.max(300, 200 + bubbleData.length * 20)}
          />
        )}
        {viewMode === 'audienceShare' && <AudienceShareBarChart data={barData} />}
      </div>
    </FullWidthChart>
  )
}

export default InterestSection
